import productsQueries from '@/api/modules/productsQueries'
import { notificationService } from '@/services/notificationService'
import { useProductsStore } from '@/stores/configurations/products'
import { handleApiError } from '@/utils/apiHelpers'
import { defaultBuildFiltersParams, defaultTranslate, normalizePaginationParams } from '@/utils/utils'

class ProductsService {
    constructor({ store = useProductsStore(), t = defaultTranslate } = {}) {
        this.store = store
        this.t = t
    }

    async fetchProductTypes({ filters, pagination } = {}) {
        const { store, t } = this
        store.setProductsLoading(true)

        const params = {
            ...normalizePaginationParams(pagination),
            ...defaultBuildFiltersParams(filters)
        }

        try {
            const response = await productsQueries.fetchProductTypes(params)
            const { data } = response

            if (data) {
                store.setProducts(data.results || [])
                store.setProductsMeta({
                    count: data.count || 0,
                    current: data.current || 1,
                    next: data.next,
                    previous: data.previous
                })
            }

            return response
        } catch (error) {
            notificationService.error(t('configurations.products.pages.main.errors.fetchList'))
            throw error
        } finally {
            store.setProductsLoading(false)
        }
    }

    async fetchProductType(productId) {
        try {
            const response = await productsQueries.fetchProductType(productId)
            this.store.setCurrentProduct(response?.data || null)
            return response
        } catch (error) {
            notificationService.error(this.t('configurations.products.pages.edit.errors.fetchProduct'))
            throw error
        }
    }

    async fetchProductProductionProperties(params = {}) {
        try {
            const response = await productsQueries.fetchProductProductionProperties(params)
            this.store.setProductProductionProperties(response.data.results || [])
            return response
        } catch (error) {
            notificationService.error(this.t('configurations.products.pages.edit.errors.fetchProductionProperties'))
            throw error
        }
    }

    async fetchProductComponents(params = {}) {
        try {
            const response = await productsQueries.fetchProductComponents(params)
            this.store.setProductComponents(response.data.results || [])
            return response
        } catch (error) {
            notificationService.error(this.t('configurations.products.pages.edit.errors.fetchComponents'))
            throw error
        }
    }

    async fetchProductionProcesses(params = {}) {
        try {
            const response = await productsQueries.fetchProductionProcesses(params)
            this.store.setProductionProcesses(response.data.results || [])
            return response
        } catch (error) {
            notificationService.error(this.t('configurations.products.pages.edit.errors.fetchProcesses'))
            throw error
        }
    }

    async fetchProductPropertiesByProductType(productTypeId, params = {}) {
        try {
            const response = await productsQueries.fetchProductPropertiesByProductType(productTypeId, params)
            this.store.setProductProperties(response.data.results || [])
            return response
        } catch (error) {
            notificationService.error(this.t('configurations.products.pages.edit.errors.fetchProperties'))
            throw error
        }
    }

    async fetchProductProperties(params = {}) {
        try {
            const response = await productsQueries.fetchProductProperties(params)
            this.store.setAllProductProperties(response.data.results || [])
            return response
        } catch (error) {
            notificationService.error(this.t('configurations.products.pages.edit.errors.fetchAllProperties'))
            throw error
        }
    }

    async updateProduct(productId, payload) {
        try {
            const response = await productsQueries.updateProduct(productId, payload)
            notificationService.success(this.t('configurations.products.pages.edit.sections.notifications.updateSuccess'))
            return response
        } catch (error) {
            notificationService.error(this.t('configurations.products.pages.edit.errors.update'))
            throw error
        }
    }
}

export const useProductsService = (options) => new ProductsService(options)
